import type { ServerToClientEvent } from '../types';

const ELEMENT_SELECTOR = 'script[data-clientjs]';
/**
 * We need to wait for slightly longer than 0ms. Waiting 4ms is enough
 * to guarantee the browser has processed any pending layout operations
 * that may yield further DOM content.
 */
const DOM_RENDER_MS = 4;

type ServerToClientEventHandler = (payload: ServerToClientEvent) => void;

export class ClientReceiver {
	private _doc: Document;

	private _fulfilled: Promise<void>;

	private _onEvent: ServerToClientEventHandler;

	constructor(doc: Document, onEvent: ServerToClientEventHandler) {
		this._doc = doc;
		this._onEvent = onEvent;

		// consume any instructions that arrived before we initialised
		this._doc
			.querySelectorAll(ELEMENT_SELECTOR)
			.forEach((el) => this.handleServerToClientElement(el));

		// Bail early if DOM is already ready, otherwise observe for new instructions until it is.
		this._fulfilled =
			this._doc.readyState === 'complete'
				? Promise.resolve()
				: new Promise((resolve) => {
						this.observe(resolve);
					});
	}

	private observe(resolve: () => void) {
		// observe the document for changes
		const observer = new MutationObserver((mutations) => this.handleMutation(mutations));
		observer.observe(this._doc.body, { attributes: false, childList: true, subtree: false });

		// only keep trying until DOM ready, then give up.
		this._doc.addEventListener(
			'DOMContentLoaded',
			() => {
				setTimeout(() => {
					observer.disconnect();
					resolve();
				}, DOM_RENDER_MS + 1);
			},
			{ once: true },
		);
	}

	private handleMutation(mutations: MutationRecord[]) {
		for (const mutation of mutations) {
			if (mutation.type === 'childList') {
				for (const node of mutation.addedNodes) {
					if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
						if (node.matches(ELEMENT_SELECTOR)) {
							setTimeout(() => this.handleServerToClientElement(node), DOM_RENDER_MS);
						}
					}
				}
			}
		}
	}

	private handleServerToClientElement(el: Element) {
		const events = el.textContent?.trim().split('\n');
		if (!events) {
			return;
		}
		for (const event of events) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const payload = JSON.parse(event) as ServerToClientEvent;
			this._onEvent(payload);
		}
	}

	fulfilled() {
		return this._fulfilled;
	}
}
